import { FeedSlot } from "@/components/vault/slot/FeedSlot";
import { useScreenStore } from "@/stores/useScreenStore";
import useVaultStore from "@/stores/zustand/vault/useVaultStore";
import { VaultObjectPreviewDto } from "@/types/data-types";
import React, { useMemo } from "react";
import { Flex } from "@radix-ui/themes";
import { splitItems } from "@/components/vault/vault-object-util";
import { UploadSlot } from "@/components/vault/slot/UploadSlot";

export const VaultFeed = React.memo(() => {
  const isMobileScreen = useScreenStore((state) => state.isMobileScreen);
  const MAX_ITEMS_PER_ROW = isMobileScreen ? 2 : 4;

  const vaultFeedData = useVaultStore((state) => state.vaultFeed);

  const { openVaultObjectViewer } = useVaultStore(
    (state) => state.vaultStoreActions,
  );

  const rowSplitItemCue = useMemo(() => {
    return splitItems({
      total: vaultFeedData.length,
      maxPerRow: MAX_ITEMS_PER_ROW,
      canUpload: false,
    });
  }, [vaultFeedData, MAX_ITEMS_PER_ROW]);

  const grid = useMemo(() => {
    let index = 0;

    return rowSplitItemCue.map((row) =>
      row.map((size) => {
        let item: VaultObjectPreviewDto | null = null;

        if (size > 0) {
          item = vaultFeedData[index] ?? null;
          index++;
        }

        return { size, item };
      }),
    );
  }, [rowSplitItemCue]);

  return (
    <Flex
      direction="column"
      align={"center"}
      className={"reveal relative w-full min-h-64"}
    >
      {grid.map((row, rowIndex) => (
        <div
          key={`row-${rowIndex}`}
          className="relative grid justify-center items-center place-items-center"
          style={{
            width: "100%",
            height: "auto",
            gridTemplateColumns: `repeat(${row.length}, ${100 / (MAX_ITEMS_PER_ROW - 0.4)}%)`,
          }}
        >
          {row.map(({ size, item }, colIndex) => {
            // NaN: 업로드 슬롯
            if (Number.isNaN(size)) {
              return <UploadSlot key={"upload-slot"} rowIndex={rowIndex} />;
            }

            // -1: 양쪽 빈 슬롯
            if (size === -1) {
              return (
                <FeedSlot
                  key={`${rowIndex}-${colIndex}-empty`}
                  className={"empty-vault-object"}
                  rowTotal={grid.length}
                  rowIndex={rowIndex}
                  colIndex={colIndex}
                  item={null}
                  size={1}
                  pointerEvents={"none"}
                />
              );
            }

            // 0: 패딩용 숨김 슬롯
            if (size === 0) {
              return (
                item && (
                  <FeedSlot
                    key={`${item.uid}-${rowIndex}-${colIndex}-${size}`}
                    className={"vault-object-hidden"}
                    rowTotal={grid.length}
                    rowIndex={rowIndex}
                    colIndex={colIndex}
                    item={null}
                    size={0}
                    pointerEvents={"none"}
                  />
                )
              );
            }

            // size > 0: 실제 아이템 슬롯
            return (
              item && (
                <FeedSlot
                  key={`${item.uid}-${item.thumbnailUrl}-${rowIndex}-${colIndex}-${size}`}
                  className={`vault-object-size-${size}`}
                  rowTotal={grid.length}
                  rowIndex={rowIndex}
                  colIndex={colIndex}
                  item={item}
                  size={size}
                  onClick={() => {
                    openVaultObjectViewer(item.uid);
                  }}
                />
              )
            );
          })}
        </div>
      ))}
    </Flex>
  );
});

VaultFeed.displayName = "VaultFeed";
